'use client';

import { useParams, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState, useTransition } from 'react';
import { toast } from 'sonner';
import { getOrder } from '@/features/orders/presentation/actions/orders/get-order';
import { usePaymentMethods } from '@/features/orders/presentation/hooks/orders/use-payment-methods';
import type { PaymentMethod } from '@/features/payments/domain/model/payment-method';
import {
  cancelPendingTotemOrder,
  createTotemOrder,
} from '@/features/totems/presentation/actions/web-totem/create-totem-order';
import {
  useTotemActions,
  useTotemCartState,
  useTotemCatalog,
} from '@/features/totems/presentation/providers/web-totem/totem-provider';
import { supabase } from '@/shared/infrastructure/supabase/client';

type CheckoutStep = 'payment' | 'processing' | 'waiting_terminal' | 'cashier' | 'qr_payment' | 'mp_redirect';

export function useTotemCheckout() {
  const router = useRouter();
  const params = useParams();
  const { totem } = useTotemCatalog();
  const { cart } = useTotemCartState();
  const { setCheckoutOpen } = useTotemActions();
  const [step, setStep] = useState<CheckoutStep>('payment');
  const [isTakeAway, setIsTakeAway] = useState(false);
  const [amountTendered, setAmountTendered] = useState<string>('');
  const [orderId, setOrderId] = useState<string | null>(null);
  const [cashNumberStr, setCashNumberStr] = useState<string | null>(null);
  const [qrData, setQrData] = useState<string | null>(null);
  const [qrMode, setQrMode] = useState<'static' | 'dynamic' | 'hybrid'>('dynamic');
  const [isPending, startTransition] = useTransition();
  const { locationId } = useParams<{ locationId: string }>();

  const locationSlug = (params?.locationId as string) || (params?.slug as string) || totem?.locations?.slug;

  // Usamos el hook de develop, pero le pasamos el locationSlug de multilocal como fallback
  const { data: paymentMethods } = usePaymentMethods({ locationIdFallback: locationId ?? locationSlug });

  // Lógica de filtrado
  const isWebTotem = Boolean(totem?.web);

  let availableMethods: PaymentMethod[] = [];

  if (locationId) {
    // Panel de caja / dashboard takeorder: agnóstico al totem, muestra todos los métodos activos
    availableMethods = paymentMethods?.paymentMethods ?? [];
  } else if (isWebTotem) {
    // Carta + Pedido móvil (QR / Web): EXCLUSIVAMENTE Mercado Pago (QR / Online Checkout)
    availableMethods = (paymentMethods?.paymentMethods ?? []).filter(
      pm =>
        pm.type === 'mercado_pago' ||
        pm.type === 'qr' ||
        pm.name.toLowerCase().includes('mercado') ||
        pm.name.toLowerCase().includes('mp')
    );
  } else {
    // Tótem físico de autoservicio: muestra los métodos configurados para ese tótem físico
    availableMethods = (paymentMethods?.paymentMethods ?? []).filter(pm => totem?.payment_methods?.includes(pm.id));
  }

  const reset = useCallback(async () => {
    if (orderId) {
      await cancelPendingTotemOrder(orderId);
    }
    setStep('payment');
    setIsTakeAway(false);
    setAmountTendered('');
    setOrderId(null);
    setCashNumberStr(null);
    setQrData(null);
    setQrMode('dynamic');
  }, [orderId]);

  const handleClose = useCallback(async () => {
    setCheckoutOpen(false);
    await reset();
  }, [setCheckoutOpen, reset]);

  const handleBack = useCallback(() => {
    if (step !== 'payment') {
      setStep('payment');
      setQrData(null);
    } else {
      handleClose();
    }
  }, [step, handleClose]);

  const handleSuccessRedirect = useCallback(
    (orderIdToRedirect: string) => {
      cart.clearCart();
      handleClose();

      const isDashboard = typeof window !== 'undefined' && window.location.pathname.includes('/takeorder');
      const locationSlug = (params?.locationId as string) || (params?.slug as string) || totem?.locations?.slug;

      if (isDashboard && locationSlug) {
        router.push(`/${locationSlug}/takeorder/success/${orderIdToRedirect}`);
      } else {
        router.push(`/orders/${orderIdToRedirect}`);
      }
    },
    [cart, handleClose, router, totem?.locations?.slug, params]
  );

  const handleOrderCreation = useCallback(
    async (methodId: string) => {
      if (!totem) return null;
      const result = await createTotemOrder({
        totemId: totem.id,
        items: cart.toOrderPayload(),
        isTakeAway,
        paymentMethodId: methodId,
        discount: cart.discount
          ? {
              discountId: cart.discount.id,
              code: cart.discount.code,
              amount: cart.discount.amount,
              type: cart.discount.type,
              method: cart.discount.method,
            }
          : undefined,
      });

      if (!result) {
        toast.error('No se pudo crear el pedido');
        setStep('payment');
        return null;
      }
      setOrderId(result.orderId);
      return result.orderId;
    },
    [totem, cart, isTakeAway]
  );

  const handleCashPayment = useCallback(
    (method: PaymentMethod) => {
      startTransition(async () => {
        setStep('processing');
        const createdOrderId = await handleOrderCreation(method.id);
        if (!createdOrderId) return;

        let orderData = null;
        try {
          orderData = await getOrder({ orderId: createdOrderId });
        } catch (error) {
          console.error('Error fetching order', error);
        }

        if (orderData) {
          import('@/features/orders/domain/service/order-util').then(({ getCashNumber }) => {
            setCashNumberStr(getCashNumber(orderData.cash_num || orderData.sort_id || 0));
          });
        }

        // Intentar impresión automática
        if (totem?.printer && orderData) {
          try {
            await fetch('http://localhost:3030/print', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                printer: { ip: totem.printer.ip, port: totem.printer.port },
                type: 'totem',
                order: orderData,
              }),
            });
          } catch (error) {
            console.error('Error al intentar imprimir el ticket', error);
          }
        }

        const isDashboard = typeof window !== 'undefined' && window.location.pathname.includes('/takeorder');
        if (isDashboard) {
          handleSuccessRedirect(createdOrderId);
        } else {
          setStep('cashier');
          setTimeout(() => {
            cart.clearCart();
            handleClose();
            router.push(`/totem-by-slug/${totem?.slug}`);
          }, 5000);
        }
      });
    },
    [cart, handleClose, handleOrderCreation, handleSuccessRedirect, router, totem]
  );

  const handleMpLinkPayment = useCallback(
    (method: PaymentMethod) => {
      startTransition(async () => {
        setStep('processing');
        const createdOrderId = await handleOrderCreation(method.id);
        if (!createdOrderId) {
          console.error('[handleMpLinkPayment] Order creation returned null/empty orderId');
          toast.error('No se pudo crear el pedido en la base de datos');
          setStep('payment');
          return;
        }
        const { createMpPreference } =
          await import('@/features/totems/presentation/actions/web-totem/create-mp-preference');
        const result = await createMpPreference(createdOrderId);

        if (!result.ok) {
          console.error('[handleMpLinkPayment] Preference generation failed:', result);
          toast.error(result.error || 'No se pudo generar el link de Mercado Pago', {
            duration: 8000,
          });
          setStep('payment');
          return;
        }
        setStep('mp_redirect');
        window.location.href = result.initPoint;
      });
    },
    [handleOrderCreation]
  );

  const handleQrPayment = useCallback(
    (method: PaymentMethod) => {
      if (totem?.web) {
        handleMpLinkPayment(method);
        return;
      }

      startTransition(async () => {
        setStep('processing');
        const createdOrderId = await handleOrderCreation(method.id);
        if (!createdOrderId) return;

        const { createQrPayment } = await import('@/features/totems/presentation/actions/web-totem/create-qr');
        const result = await createQrPayment(createdOrderId);

        if (!result) {
          toast.error('No pudimos generar el código QR en la terminal');
          setStep('payment');
          return;
        }

        setQrMode(result.mode);
        setQrData(result.qrData);
        setStep('qr_payment');
      });
    },
    [handleOrderCreation, handleMpLinkPayment, totem?.web]
  );

  const handleCardPayment = useCallback(
    (method: PaymentMethod) => {
      startTransition(async () => {
        setStep('processing');
        const createdOrderId = await handleOrderCreation(method.id);
        if (!createdOrderId) return;

        setStep('waiting_terminal');

        const { error: paymentError } = await supabase.functions.invoke('payment-operation', {
          body: { orderId: createdOrderId },
          method: 'POST',
        });

        if (paymentError) {
          toast.error('No se pudo iniciar el pago en el terminal');
          setStep('payment');
          return;
        }
      });
    },
    [handleOrderCreation]
  );

  useEffect(() => {
    if ((step !== 'waiting_terminal' && step !== 'qr_payment') || !orderId) return;

    let paymentId: string | null = null;
    let pollInterval: NodeJS.Timeout | null = null;

    const setupSubscription = async () => {
      const { data: order } = await supabase
        .from('orders')
        .select('payment_id, order_payments(status)')
        .eq('id', orderId)
        .single();

      paymentId = order?.payment_id ?? null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase returns array for 1-to-many
      const payments: any[] = Array.isArray(order?.order_payments) ? order.order_payments : [order?.order_payments];
      const hasCompleted = payments.some(p => p?.status === 'completed');

      if (hasCompleted) {
        setTimeout(() => handleSuccessRedirect(orderId), 3000);
        return;
      }

      if (!paymentId) return;

      const channel = supabase
        .channel(`order-payment:${paymentId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'order_payments',
            filter: `id=eq.${paymentId}`,
          },
          payload => {
            const status = (payload.new as { status?: string })?.status;
            if (status === 'completed') {
              setTimeout(() => handleSuccessRedirect(orderId), 3000);
            }
            if (status === 'canceled' || status === 'error') {
              toast.error('El pago fue rechazado');
              setStep('payment');
            }
          }
        )
        .subscribe();

      // Fallback polling
      pollInterval = setInterval(async () => {
        if (!paymentId) return;
        const { data: paymentData } = await supabase
          .from('order_payments')
          .select('status')
          .eq('id', paymentId)
          .single();

        const status = paymentData?.status;
        if (status === 'completed') {
          setTimeout(() => handleSuccessRedirect(orderId), 3000);
        } else if (status === 'canceled' || status === 'error') {
          toast.error('El pago fue rechazado');
          setStep('payment');
        }
      }, 5000);

      return () => {
        supabase.removeChannel(channel);
        if (pollInterval) clearInterval(pollInterval);
      };
    };

    const cleanupPromise = setupSubscription();

    return () => {
      cleanupPromise.then(cleanup => cleanup?.());
    };
  }, [step, orderId, handleSuccessRedirect]);

  return {
    step,
    setStep,
    isTakeAway,
    setIsTakeAway,
    amountTendered,
    setAmountTendered,
    availableMethods: availableMethods ?? [],
    isPending,
    orderId,
    cashNumberStr,
    qrData,
    qrMode,
    handleCashPayment,
    handleQrPayment,
    handleCardPayment,
    handleMpLinkPayment,
    handleClose,
    handleBack,
    reset,
  };
}
